import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Search, Trash2, Eye, ChevronLeft, ChevronRight, DollarSign, Upload, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CasesList() {
  const navigate = useNavigate();
  const token = localStorage.getItem('vendor_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const fileRef = useRef(null);

  const [bills, setBills] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selected, setSelected] = useState([]);

  const fetchBills = useCallback(async () => {
    const params = new URLSearchParams({ page, limit: 30 });
    if (search) params.append('search', search);
    if (statusFilter) params.append('status', statusFilter);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    try {
      const res = await fetch(`${API}/api/lis/bills?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to load cases');
        return;
      }
      const data = await res.json();
      setBills(data.bills || []);
      setTotal(data.total || 0);
      setPages(data.pages || 0);
    } catch (err) {
      toast.error('Failed to load cases');
    }
  }, [page, search, statusFilter, dateFrom, dateTo, token]);

  useEffect(() => { fetchBills(); }, [fetchBills]);

  const deleteBill = async (id) => {
    if (!window.confirm('Delete this bill?')) return;
    await fetch(`${API}/api/lis/bills/${id}`, { method: 'DELETE', headers });
    toast.success('Deleted');
    fetchBills();
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAll = () => setSelected(selected.length === bills.length ? [] : bills.map(b => b.id));

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} selected bill(s)?`)) return;
    const res = await fetch(`${API}/api/lis/bills/bulk-delete`, { method: 'POST', headers, body: JSON.stringify({ ids: selected }) });
    if (res.ok) {
      const data = await res.json();
      toast.success(`Deleted ${data.deleted} bill(s)`);
      setSelected([]);
      fetchBills();
    } else toast.error('Bulk delete failed');
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API}/api/lis/bills/bulk-upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    const data = await res.json();
    toast.success(`Uploaded ${data.created} bills${data.skipped ? `, skipped ${data.skipped} duplicate(s)` : ''}`);
    if (data.errors?.length) toast.error(`${data.errors.length} issues, check console`, { description: data.errors.slice(0, 3).join('; ') });
    fetchBills();
    e.target.value = '';
  };

  return (
    <div data-testid="lis-cases-list">
      <div className="flex items-center justify-between mb-4">
        <div><h1 className="text-xl font-bold text-slate-800">Cases / Bills</h1><p className="text-sm text-slate-500">{total} bills</p></div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.open(`${API}/api/lis/business/templates/bills`, '_blank')} data-testid="download-bill-template-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> Template
          </Button>
          <input type="file" ref={fileRef} accept=".csv" onChange={handleCSVUpload} className="hidden" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="bill-csv-upload-btn">
            <Upload className="w-3.5 h-3.5 mr-1" /> CSV Upload
          </Button>
          <Button size="sm" onClick={() => navigate('/vendor/lis/new-bill')} data-testid="new-bill-btn">New Bill</Button>
        </div>
      </div>

      <Card className="mb-4 border-slate-200"><CardContent className="p-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]"><Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" /><Input placeholder="Search reg no, order #, name, mobile..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9" data-testid="search-bills" /></div>
          <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[130px] h-9"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="due">Due</SelectItem><SelectItem value="no_due">Paid</SelectItem></SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[150px] h-9" />
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[150px] h-9" />
        </div>
      </CardContent></Card>

      {selected.length > 0 && (
        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between" data-testid="bills-selection-bar">
          <span className="font-medium text-blue-700 text-sm">{selected.length} bill(s) selected</span>
          <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={bulkDelete} data-testid="bulk-delete-bills-btn">
            <Trash2 className="w-3 h-3 mr-1" /> Delete Selected
          </Button>
        </div>
      )}

      <Card className="border-slate-200"><div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b bg-slate-50">
        <th className="p-3 text-left w-10"><input type="checkbox" checked={selected.length === bills.length && bills.length > 0} onChange={toggleAll} data-testid="select-all-bills" /></th>
        <th className="p-3 text-left font-medium text-slate-600">Reg #</th>
        <th className="p-3 text-left font-medium text-slate-600">Date</th>
        <th className="p-3 text-left font-medium text-slate-600">Patient</th>
        <th className="p-3 text-left font-medium text-slate-600">Referred By</th>
        <th className="p-3 text-right font-medium text-slate-600">Total</th>
        <th className="p-3 text-right font-medium text-slate-600">Paid</th>
        <th className="p-3 text-right font-medium text-slate-600">Balance</th>
        <th className="p-3 text-center font-medium text-slate-600">Status</th>
        <th className="p-3 text-right font-medium text-slate-600 w-20">Actions</th>
      </tr></thead><tbody>
        {bills.map(b => (
          <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/vendor/lis/bill/${b.id}`)}>
            <td className="p-3" onClick={e => e.stopPropagation()}><input type="checkbox" checked={selected.includes(b.id)} onChange={() => toggleSelect(b.id)} data-testid={`select-bill-${b.id}`} /></td>
            <td className="p-3 font-mono text-xs">#{b.reg_no}</td>
            <td className="p-3 text-slate-500 text-xs">{(b.created_at || '').slice(0, 10)}</td>
            <td className="p-3"><div><p className="font-medium text-slate-800 text-xs">{b.patient_name}</p>{b.patient_mobile && <p className="text-[10px] text-slate-400">{b.patient_mobile}</p>}</div></td>
            <td className="p-3 text-xs text-slate-500">{b.referred_by || '-'}</td>
            <td className="p-3 text-right font-medium">Rs.{(b.total || 0).toLocaleString()}</td>
            <td className="p-3 text-right text-emerald-600">Rs.{(b.amount_received || 0).toLocaleString()}</td>
            <td className="p-3 text-right text-red-600">Rs.{(b.balance || 0).toLocaleString()}</td>
            <td className="p-3 text-center"><Badge className={b.status === 'no_due' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{b.status === 'no_due' ? 'Paid' : 'Due'}</Badge></td>
            <td className="p-3 text-right" onClick={e => e.stopPropagation()}>
              <div className="flex gap-1 justify-end">
                <button onClick={() => navigate(`/vendor/lis/bill/${b.id}`)} className="p-1 text-slate-400 hover:text-blue-600"><Eye className="w-3.5 h-3.5" /></button>
                <button onClick={() => deleteBill(b.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </td>
          </tr>
        ))}
        {bills.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-slate-400">No bills found.</td></tr>}
      </tbody></table></div>
      {pages > 1 && <div className="flex items-center justify-between p-3 border-t"><span className="text-xs text-slate-500">Page {page}/{pages}</span><div className="flex gap-1"><Button variant="outline" size="sm" disabled={page<=1} onClick={()=>setPage(p=>p-1)}><ChevronLeft className="w-4 h-4" /></Button><Button variant="outline" size="sm" disabled={page>=pages} onClick={()=>setPage(p=>p+1)}><ChevronRight className="w-4 h-4" /></Button></div></div>}
      </Card>
    </div>
  );
}
